import { CURRENCIES } from "../../../../../variables/common"

export const groupTerminalsByCountry = (data, defaultTerminalIds = []) => {
  if (!Array.isArray(data) || data.length === 0) return []

  const allowedSelectedIds = new Set(defaultTerminalIds.slice(0, 3))

  const grouped = {}

  data.forEach((item) => {
    const country = item.country

    if (!grouped[country]) {
      grouped[country] = {
        country: item.country,
        country_code: item.country_code,
        terminals: [],
      }
    }

    const terminalsWithSelection = item.terminals.map((terminal) => ({
      ...terminal,
      is_selected: allowedSelectedIds.has(terminal.id),
    }))

    grouped[country].terminals.push(...terminalsWithSelection)
  })

  return Object.values(grouped)
}

export const getAllTerminalIds = (dataArray = [], defaultIds = []) => {
  let allTerminalIds = []

  if (Array.isArray(dataArray)) {
    dataArray.forEach((item) => {
      if (item && Array.isArray(item.terminal_ids)) {
        allTerminalIds = allTerminalIds.concat(item.terminal_ids)
      }
    })
  }

  allTerminalIds = [...new Set(allTerminalIds)]

  if (Array.isArray(defaultIds)) {
    defaultIds.forEach((id) => {
      if (!allTerminalIds.includes(id)) {
        allTerminalIds.push(id)
      }
    })
  }

  return allTerminalIds
}

const parseFlightDetails = (htmlString) => {
  if (!htmlString) return null
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, "text/html")

  const airlineImgUrl = doc.querySelector(".kiwi-airline-logo")?.src || ""

  let match = airlineImgUrl.match(/([^/]+\.png)$/)
  let airlineImg = match ? match[1] : "0A.png"

  if (airlineImg === ".png") airlineImg = "0A.png"

  airlineImg = `/airlines/${airlineImg}`

  const flightNumber = (doc.querySelector(".kiwi-airline-name")?.textContent || "")
    .replace(/\s*Flight №:\s*/, "")
    .trim()

  const duration = doc.querySelector(".kiwi-title-duration")?.textContent.trim() || ""

  const terminals = Array.from(doc.querySelectorAll(".kiwi-terminal-wrapper")).map((wrapper) => {
    const code = wrapper.querySelector(".kiwi-termianl-name")?.textContent.trim() || ""
    const time = wrapper.querySelector(".kiwi-flight-time")?.textContent.trim() || ""
    return { code, time }
  })

  return { airlineImg, flightNumber, duration, terminals }
}

export const parseBaggageDetails = (htmlString) => {
  if (!htmlString) return { departure: {}, return: {} }
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, "text/html")

  const sections = Array.from(doc.querySelectorAll(".kiwi-bags-wrapper > .kiwi-title"))
  const tables = Array.from(doc.querySelectorAll(".kiwi-bags-wrapper > table"))
  const result = {}

  sections.forEach((section, index) => {
    const type = section.textContent.toLowerCase().includes("departure") ? "departure" : "return"
    const rows = Array.from(tables[index].querySelectorAll("tr"))
    const bags = {}
    rows.forEach((row) => {
      const tds = row.querySelectorAll("td")
      const name = tds[0].getAttribute("title") || tds[0].textContent
      const included = tds[2].textContent.trim().toLowerCase() === "included"
      if (name) {
        if (name.toLowerCase().includes("hand")) bags.handBaggage = included
        if (name.toLowerCase().includes("hold")) bags.holdBaggage = included
      }
    })
    result[type] = bags
  })

  return result
}

export const prepareSingleTicketCards = (tour, t) => {
  const departureCard = tour.depart_available
    ? {
        requestId: tour?.request_id,
        session: tour?.session,
        type: "departure",
        label: t("airform.departure_date"),
        date: tour.checkin,
        weekDay: tour.checkin_week,
        details: parseFlightDetails(tour?.depart_details),
        from: {
          code: tour.depart_terminal_code,
          city: tour.depart_terminal,
        },
        to: {
          code: tour.arrival_terminal_code,
          city: tour.arrival_terminal,
        },
        airlineCode: tour.depart_company_code,
        time: {
          departure: tour.depart_time_departure,
          arrival: tour.depart_time_arrival,
        },
        duration: tour.depart_duration,
        airline: tour.depart_company_name || "",
        classSeat: tour.class_seat,
        isNight: tour.depart_night,
        isDirect: tour.depart_is_direct,
        charter: tour?.depart_charter || "",
        currency: tour?.currency || CURRENCIES[0].code,
      }
    : null

  const returnCard = tour.retur_available
    ? {
        requestId: tour?.request_id,
        session: tour?.session,
        type: "return",
        label: t("airform.departure_date_back"),
        details: parseFlightDetails(tour?.retur_details),
        date: tour.checkout,
        weekDay: tour.checkout_week,
        from: {
          code: tour.arrival_terminal_code,
          city: tour.arrival_terminal,
        },
        to: {
          code: tour.depart_terminal_code,
          city: tour.depart_terminal,
        },
        time: {
          departure: tour.retur_time_departure,
          arrival: tour.retur_time_arrival,
        },
        duration: tour.retur_duration,
        airline: tour.retur_company_name || "",
        classSeat: tour.class_seat,
        isNight: tour.retur_night,
        isDirect: tour.retur_is_direct,
        charter: tour?.retur_charter || "",
        currency: tour?.currency || CURRENCIES[0].code,
        airlineCode: tour.retur_company_code,
      }
    : null

  return [departureCard, returnCard].filter(Boolean)
}

export const parsePassengersCount = (peoples) => {
  if (!peoples) return 0

  const match = peoples.match(/\d+/)
  return match ? Number(match[0]) : 0
}

export const mergeDefaultIds = (selectedItems, defaultIds) => {
  const ids = selectedItems?.map((item) => item.id) || []

  defaultIds?.forEach((id) => {
    if (!ids.includes(id)) {
      ids.push(id)
    }
  })

  return ids
}

export const collectIds = (items) => {
  const ids = items.map((item) => item.id) || []
  return ids
}

export const updateTerminals = (source, incoming) => {
  const sourceMap = new Map(source.map((item) => [item.iata, item]))

  return incoming.map((inItem) => {
    const baseItem = sourceMap.get(inItem.iata)

    if (!baseItem) {
      return {
        ...inItem,
        terminals: inItem.terminals.map((t) => ({
          ...t,
          is_selected: false,
        })),
      }
    }

    const hasSelected = baseItem.terminals?.some((t) => t.is_selected)
    if (hasSelected) {
      return baseItem
    }

    return {
      ...baseItem,
      terminals: inItem.terminals.map((t) => ({
        ...t,
        is_selected: false,
      })),
    }
  })
}

export const buildChildrenArray = (childrenCount = 0, infantsCount = 0) => {
  const result = []

  for (let i = 0; i < infantsCount; i++) {
    result.push(1)
  }

  for (let i = 0; i < childrenCount; i++) {
    result.push(7)
  }

  return result
}

export const customFormatDate = (dateStr, locale) => {
  if (!dateStr) return ""

  const [day, month, year] = dateStr.split(".")
  const date = new Date(year, month - 1, day)

  let result = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(date)

  result = result.replace(
    /(\d{2})\s(.+)/,
    (_, d, m) => `${d} ${m.charAt(0).toUpperCase()}${m.slice(1)}`
  )

  return result
}

export const formatDateArray = (arr, locale) => {
  if (!arr || arr.length === 0) return ""

  const validDates = arr.filter((date) => date !== null)

  if (validDates.length === 0) return ""
  if (validDates.length === 1) return customFormatDate(validDates[0], locale) ?? ""

  return `${customFormatDate(validDates[0], locale) ?? ""} - ${customFormatDate(validDates[1], locale) ?? ""}`
}

export const collectSelectedTerminals = (countries = []) => {
  return countries
    .filter((country) => country.is_selected)
    .flatMap((country) =>
      country.terminals.map((terminal) => ({
        id: terminal.id,
        country_name: country.country,
        iata: terminal.iata,
        name: terminal.name,
        is_selected: terminal.is_selected,
      }))
    )
}
